/* eslint-disable jsx-a11y/click-events-have-key-events */
import { format } from 'date-fns';
import React, { useState } from 'react';
import { Tooltip } from 'react-leaflet';
import { StyledBigTooltipInner } from '../../Styled';
import { getValueSafely } from 'utils/utils';
import infoImg from '../../icons/info.svg';
import closeImg from '../../icons/close.svg';
import AccuracyRangeSpan from './InfoBoxSpans/AccuracyRangeSpan';
import BatteryReadoutSpan from './InfoBoxSpans/BatteryReadoutSpan';
import LocationSpan from './InfoBoxSpans/LocationSpan';
import TooltipDescriptionPanel from './TooltipDescriptionPanel';
import LastSeenReadoutSpan from './InfoBoxSpans/LastSeenReadoutSpan';
import DefaultCell from 'components/DataTable/cells/DefaultCell';
import { alertErrorMessage } from 'utils/alerts';
import AssetStatusReadoutSpan from './InfoBoxSpans/AssetStatusReadoutSpan';
import { useMapStore } from 'contexts/RootStore.context';
import { Asset } from 'types/asset';
import { UpdateStatusModal } from 'views/assets/AssetFlyout/UpdateStatusModal';
import { getAssetById } from 'api/assets.api';
import { observer } from 'mobx-react';

type LargeInfoBoxTooltipProps = {
  asset: Asset;
  handleCloseInfoBox: () => void;
};

function getCreatedDateString(asset: Asset) {
  if (!asset.creation_timestamp) return '––';

  const addedOnDate = asset.creation_timestamp
    ? format(new Date(asset.creation_timestamp), 'dd/LL/yyyy - HH:mm')
    : '––';

  return addedOnDate;
}

export const LargeInfoBoxTooltip = observer(({ asset, handleCloseInfoBox }: LargeInfoBoxTooltipProps) => {
  const [currentAsset, setCurrentAsset] = useState(asset);
  const [updateAssetStatusModalOpen, setUpdateAssetStatusModalOpen] = useState(false);
  const { assetStatuses, filterForm, loadAssets } = useMapStore()

  function handleCloseModal() {
    setUpdateAssetStatusModalOpen(false);
  }

  function handleSuccessfulUpdate() {
    getAssetById(currentAsset.asset_id).then(asset => {
      if (asset)
        setCurrentAsset(asset)
      setUpdateAssetStatusModalOpen(false)

      //load assets if the filter does not include all statuses
      if (filterForm.$.assetStatuses.value.length < assetStatuses.length + 1) {
        loadAssets()
      }
    })
  }

  function handleUpdateAssetStatusModal() {
    if (assetStatuses.length === 0) {
      alertErrorMessage("Asset Status can't be updated.");
      return;
    }
    setUpdateAssetStatusModalOpen(true);
  }

  return (
    <>
      <Tooltip
        className="tooltip-large"
        interactive={true}
        direction="right"
        offset={[20, 0]}
        opacity={1}
        permanent
      >
        <StyledBigTooltipInner id={`asset-info-tooltip-${currentAsset.asset_id}`}>
          <img
            alt="close"
            className="close-button"
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
            role="button"
            src={closeImg}
            onClick={() => {
              handleCloseInfoBox();
            }}
          />
          <div className="tooltip-inner-container">
            <div className="tooltip-inner-content">
              <div className="information-line">
                <h4>Asset Name:</h4>

                <span>{getValueSafely(currentAsset.asset_name)}</span>
              </div>
              <div className="information-line">
                <h4>Asset Type:</h4>
                <span>{<DefaultCell value={currentAsset.asset_type.name} />}</span>
              </div>
              <div className="information-line">
                <h4>Asset ID:</h4>
                <span style={{ overflowX: 'hidden', textOverflow: 'ellipsis' }}>
                  {getValueSafely(currentAsset.asset_id)}
                </span>
              </div>
              <div className="information-line">
                <h4>Added On:</h4>
                <span>{getCreatedDateString(asset)}</span>
              </div>
              <div className="information-line">
                <h4>Owner:</h4>
                <span>
                  <DefaultCell value={currentAsset.owner.name} />
                </span>
              </div>

              <TooltipDescriptionPanel asset={currentAsset} />
              {assetStatuses.length > 0 && (
                <div className="information-line">
                  <h4>Status:</h4>
                  <span className="asset-status-span">
                    <AssetStatusReadoutSpan
                      value={currentAsset.status_assignment?.asset_status?.description}
                    />
                    <button onClick={handleUpdateAssetStatusModal}>
                      Update Asset Status
                    </button>
                  </span>
                </div>
              )}
            </div>

            <div className="hr"></div>

            <div className="tooltip-inner-content">
              <div className="information-sentance">
                <img src={infoImg} alt="information" />
                <p>Please note, estimated information was derived from the beacon.</p>
              </div>
              <div className="information-line">
                <h4>Last Seen:</h4>
                <LastSeenReadoutSpan timestamp={currentAsset.last_location_update_timestamp} />
              </div>
              <div className="information-line">
                <h4>Accuracy Range:</h4>
                <AccuracyRangeSpan asset={currentAsset} />
              </div>

              <div className="information-line">
                <h4>Battery:</h4>
                <BatteryReadoutSpan asset={currentAsset} />
              </div>
              <div className="information-line">
                <h4>Location:</h4>
                <LocationSpan asset={currentAsset} />
              </div>
              <div className="information-line">
                <h4>Beacon ID:</h4>
                <span>{getValueSafely(currentAsset.assigned_beacon!.id)}</span>
              </div>
              <div className="information-line">
                <h4>Beacon Type:</h4>
                <span>
                  {getValueSafely(currentAsset.assigned_beacon!.beacon_type)}
                  {currentAsset.assigned_beacon!.import_info.source &&
                    ` - ${currentAsset.assigned_beacon!.import_info.source}`}
                </span>
              </div>
            </div>
          </div>
        </StyledBigTooltipInner>
      </Tooltip>
      {updateAssetStatusModalOpen && (
        <UpdateStatusModal
          asset={currentAsset}
          onClose={handleCloseModal}
          onUpdate={handleSuccessfulUpdate}
        />
      )}
    </>
  );
})
